import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Popconfirm, Table, Tooltip } from 'antd';
import {
  CopyOutlined,
  EditOutlined,
  EyeOutlined,
  WarningOutlined,
  PrinterOutlined,
  ContainerOutlined
} from '@ant-design/icons';
import { useAuthContext } from '../../contexts/AuthContext';
import { useErrorMessage } from '../../utils/errorMessage';

const iconSize = 16;

export const Datatable = ({
  resourceName,
  path,
  columns,
  customActionColumn,
  populate,
  extraQuery,
  forceRefresh,
  showAction,
  editAction,
  deleteAction,
  duplicateAction,
  printAction,
  onDoubleClickAction,
  scroll,
  expandable,
  rowKey,
  archiveFilter,
  setResourceData,
  rowSelection,
  navigate
}) => {
  const location = useLocation();
  const { pathname } = location;
  const { t } = useTranslation();
  const { dispatchAPI } = useAuthContext();
  const { message } = useErrorMessage();
  const [isLoading, setIsLoading] = useState(false);
  const [resources, setResources] = useState([]);
  const params = new URLSearchParams(location.search);
  const searchValue = params.get('k');
  const currentPage = Number(params.get('p') || 1);
  const pageSize = Number(params.get('pS') || 10);
  const currentFilters = params.get('f');
  const currentSorter = params.get('s');
  const [pagination, setPagination] = useState({
    pageSize: 10,
    total: 0,
    showTotal: (total, range) => `${range[1]} sur ${total} éléments`,
    showSizeChanger: true
  });

  const fetchData = useCallback(
    async (page = pagination) => {
      setIsLoading(true);

      const searchURL = searchValue ? `/search/${searchValue}` : null;

      let sortingParameter;
      if (currentSorter) sortingParameter = `sort=${currentSorter}&`;
      let filterParameter;
      if (currentFilters)
        filterParameter = `${currentFilters.replaceAll('__', '&')}`;
      try {
        const { data, headers } = await dispatchAPI('GET', {
          url: `/${resourceName}${searchURL || ''}?${
            extraQuery ? `${extraQuery}&` : ''
          }${archiveFilter ? `is_archived=true&` : 'is_archived!=true&'}${
            sortingParameter || 'sort=-reference&'
          }${filterParameter || ''}${
            populate ? `populate=${populate}&` : ''
          }limit=${pageSize}&skip=${(currentPage - 1) * pageSize}`
        });
        setPagination({
          ...page,
          total: Number.parseInt(headers?.['x-total-count'] || 0, 10)
        });
        setResources(data);
        if (setResourceData) setResourceData(data);
      } catch (e) {
        message(e);
      }
      setIsLoading(false);
    },
    [
      searchValue,
      currentPage,
      pageSize,
      currentSorter,
      currentFilters,
      forceRefresh,
      extraQuery,
      archiveFilter
    ]
  );

  const deleteResource = async (id) => {
    try {
      await dispatchAPI('PATCH', {
        url: `/${resourceName}/${id}`,
        body: { is_archived: true, status: 'ARCHIVED' }
      });
      await fetchData();
    } catch (e) {
      message(e);
    }
  };

  const duplicateResource = async (id) => {
    try {
      await dispatchAPI('POST', { url: `/${resourceName}/duplicate/${id}` });
      await fetchData();
    } catch (e) {
      message(e);
    }
  };

  const printResource = async (id) => {
    try {
      await dispatchAPI('GET', { url: `/files/${id}` });
      await fetchData();
    } catch (e) {
      message(e);
    }
  };

  const handlePageChange = (page, filters, sorters = {}) => {
    let sortingParameter;
    if (sorters) {
      if (!sorters.order) {
        sortingParameter = null;
      } else if (sorters.order === 'descend') {
        sortingParameter = `&s=-${sorters.columnKey}`;
      } else {
        sortingParameter = `&s=${sorters.columnKey}`;
      }
    }
    let filterParameter = '';
    Object.entries(filters || {}).forEach((el) => {
      if (el[1]?.length) filterParameter += `${el[0]}=${[...el[1]]}__`;
    });
    const pageSizeParam = page?.pageSize ? `&pS=${page.pageSize}` : '';
    const filterParam = filterParameter ? `&f=${filterParameter}` : '';
    const searchParam = searchValue ? `&k=${searchValue}` : '';
    // The query is rebuilt from scratch, so anything the page itself put there
    // (an open tab, for instance) has to be carried over or paginating would
    // silently drop it.
    const foreignParams = new URLSearchParams(location.search);
    ['p', 'pS', 's', 'f', 'k'].forEach((key) => foreignParams.delete(key));
    const foreignParam = foreignParams.toString()
      ? `&${foreignParams.toString()}`
      : '';
    const searchString = `?p=${page.current}${pageSizeParam}${
      sortingParameter || ''
    }${filterParam}${searchParam}${foreignParam}`;
    navigate({
      pathname,
      search: searchString
    });
  };

  useEffect(() => {
    (async () => {
      await fetchData();
    })();
  }, [fetchData]);

  const actionColumn = [
    {
      key: 'action',
      align: 'right',
      fixed: 'right',

      className: customActionColumn ? 'custom-action-column' : '',

      render: (record) => (
        <div className="table-actions">
          {duplicateAction && (
            <CopyOutlined
              onClick={() => duplicateResource(record?.[rowKey])}
              style={{ fontSize: iconSize }}
            />
          )}
          {showAction && (
            <Link
              to={{
                pathname: showAction.pathname
                  ? showAction.pathname(record)
                  : `${path || pathname}/show/${record?.[rowKey]}`
              }}
            >
              <Tooltip title={t('datatable.column.action.show.hover')}>
                <EyeOutlined style={{ fontSize: iconSize }} />
              </Tooltip>
            </Link>
          )}
          {editAction && (
            <Link
              to={{
                pathname: editAction.pathname
                  ? editAction.pathname(record)
                  : `${path || pathname}/edit/${record?.[rowKey]}`
              }}
            >
              <Tooltip title={t('datatable.column.action.edit.hover')}>
                <EditOutlined style={{ fontSize: iconSize }} />
              </Tooltip>
            </Link>
          )}
          {printAction && (
            <Tooltip title={t('datatable.column.action.print.hover')}>
              <PrinterOutlined
                onClick={() => printResource(record?.[rowKey])}
                style={{ fontSize: iconSize }}
              />
            </Tooltip>
          )}
          {deleteAction && (
            <Popconfirm
              title={t('datatable.column.action.archive.title')}
              okText={t('datatable.column.action.archive.ok')}
              okButtonProps={{ type: 'danger' }}
              cancelText={t('datatable.column.action.archive.cancel')}
              onConfirm={() => deleteResource(record?.[rowKey])}
              icon={<WarningOutlined />}
            >
              <Tooltip
                title={t('datatable.column.action.archive.hover')}
                placement="bottom"
              >
                <ContainerOutlined
                  style={{ fontSize: iconSize }}
                  type="delete"
                />
              </Tooltip>
            </Popconfirm>
          )}
        </div>
      )
    }
  ];

  const adjustedColumns = columns.map((col) => {
    let order;
    let orderKey;
    if (currentSorter) {
      order = 'ascend';
      orderKey = currentSorter;
    }
    if (currentSorter?.startsWith('-')) {
      order = 'descend';
      orderKey = currentSorter.substring(1);
    }
    const filters = {};
    if (currentFilters) {
      const filtersList = currentFilters.split('__');
      filtersList.forEach((f) => {
        if (f.split('=').length) {
          const [key, values] = f.split('=');
          filters[key] = values;
        }
      });
    }

    return {
      ...col,
      sortOrder: col.key === orderKey ? order : false,
      filteredValue: filters[col.key] ? filters[col.key].split(',') : []
    };
  });

  return (
    <Table
      style={{ whiteSpace: 'nowrap' }}
      tableLayout="auto"
      scroll={scroll}
      rowKey={rowKey}
      rowClassName="rowStyle"
      onRow={(record) => ({
        ...(onDoubleClickAction
          ? {
              onDoubleClick: () =>
                onDoubleClickAction.action
                  ? onDoubleClickAction.action(record)
                  : navigate(`${path || pathname}/show/${record[rowKey]}`)
            }
          : {})
      })}
      dataSource={resources}
      loading={isLoading}
      onChange={handlePageChange}
      pagination={{ ...pagination, current: currentPage, pageSize }}
      columns={
        customActionColumn
          ? adjustedColumns
          : [...adjustedColumns, ...actionColumn]
      }
      expandable={
        !isLoading && resources.length && expandable
          ? {
              ...expandable,
              expandedRowKeys:
                expandable.expandedRowKeys || resources.map((r) => r.key)
            }
          : undefined
      }
      rowSelection={rowSelection}
    />
  );
};

Datatable.propTypes = {
  resourceName: PropTypes.string.isRequired,
  path: PropTypes.string,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      key: PropTypes.string.isRequired,
      dataIndex: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string)
      ]).isRequired,
      render: PropTypes.func,
      sorter: PropTypes.bool,
      filters: PropTypes.arrayOf(
        PropTypes.shape({
          text: PropTypes.string,
          value: PropTypes.string
        })
      )
    })
  ).isRequired,
  customActionColumn: PropTypes.bool,
  populate: PropTypes.string,
  style: PropTypes.shape({}),
  extraQuery: PropTypes.string,
  forceRefresh: PropTypes.bool,
  editAction: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      pathname: PropTypes.func
    })
  ]),
  showAction: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      pathname: PropTypes.func
    })
  ]),
  duplicateAction: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      pathname: PropTypes.func
    })
  ]),
  printAction: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      pathname: PropTypes.func
    })
  ]),
  deleteAction: PropTypes.bool,
  onDoubleClickAction: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      action: PropTypes.func
    })
  ]),
  scroll: PropTypes.shape({}),
  expandable: PropTypes.shape({
    expandedRowKeys: PropTypes.arrayOf(PropTypes.string)
  }),
  rowKey: PropTypes.string,
  archiveFilter: PropTypes.bool,
  setResourceData: PropTypes.func,
  rowSelection: PropTypes.shape({}),
  navigate: PropTypes.func.isRequired
};

Datatable.defaultProps = {
  path: null,
  customActionColumn: false,
  populate: null,
  style: null,
  extraQuery: null,
  forceRefresh: null,
  editAction: true,
  showAction: true,
  duplicateAction: false,
  printAction: false,
  deleteAction: true,
  onDoubleClickAction: true,
  scroll: null,
  expandable: undefined,
  rowKey: '_id',
  archiveFilter: false,
  setResourceData: null,
  rowSelection: undefined
};
